import React from 'react'

import {
  type ContextInputBlurredProperties,
  type InputPopulationLevel,
  trackContextInputBlurred,
  type TrackingProperties,
} from './tracking'
import { type TrackingContextState, TrackingReactContext } from './tracking_context'

export interface InputTracking {
  /** Properties included in analytics track event. */
  trackingProperties?: TrackingProperties

  /** Set he fieldName for this blurable. Required because we can't infer field names. */
  trackingFieldName: string

  /** Level of population of the field, from no content to content that is submittable. */
  trackingPopulationLevel: InputPopulationLevel
}

/**
 * Wrap an existing element with an onBlur handler. This should be used for wrapping <input/>
 * to understand how much progress a user has made through a form (ex. the cart).
 *
 * We add 2 props the the wrapped class to make sure sufficient data is tracked:
 * - `trackingFieldName` - The name used to identify the field in the data.
 * - `trackingIsPopulate` - To allow the calling code to mark a field populated.
 *
 * The `isPopulated` prop is useful to determine if a user has actually completed a field
 * or stopped part way. A good example of this is the Stripe credit card input which
 * is 1 input with multiple fields that need to be completed.
 *
 * @return {React.ComponentClass<T & InputTracking, {}>}
 * @param Clazz
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function asTrackedInput<T extends { onBlur?: (args: any) => any }>(
  Clazz: React.ComponentType<T>,
): React.ComponentClass<T & InputTracking> {
  return class InnerTrackedBlurable extends React.Component<T & InputTracking> {
    public override render() {
      return (
        <TrackingReactContext.Consumer>
          {(context: TrackingContextState) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const onBlur = (args: any) => {
              const trackingProps: ContextInputBlurredProperties = {
                ...context.properties,
                ...this.props.trackingProperties,
                fieldName: this.props.trackingFieldName,
                populationLevel: this.props.trackingPopulationLevel,
              }

              if (context.name) {
                trackingProps.trackingContext = context.name
              }

              trackContextInputBlurred(trackingProps)

              if (this.props.onBlur) {
                this.props.onBlur(args)
              }
            }

            // We need to strip the trackingProperties prop before passing to the child component.
            const {
              trackingProperties,
              trackingPopulationLevel,
              trackingFieldName,
              ...innerProps
            } = this.props
            // oxlint-disable-next-line react-js/jsx-no-bind
            return <Clazz {...(innerProps as unknown as T)} onBlur={onBlur} />
          }}
        </TrackingReactContext.Consumer>
      )
    }
  }
}
